import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import MainLayout from '../../components/layout/MainLayout';
import Pagination from '../../components/common/Pagination';
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';

interface Affiliate {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  company_name: string | null;
  referral_code: string;
  code: string;
  status: 'pending' | 'active' | 'suspended';
  total_referrals: number;
  total_earnings: number;
  commission_rate: number;
  created_at: string;
}

export default function AffiliatesPage() {
  const [affiliates, setAffiliates] = useState<Affiliate[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'pending' | 'active' | 'suspended'>('all');
  const [selectedAffiliate, setSelectedAffiliate] = useState<Affiliate | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [editingCommission, setEditingCommission] = useState(false);
  const [newCommissionRate, setNewCommissionRate] = useState<number>(20);

  useEffect(() => {
    loadAffiliates();
  }, [filter]);

  const loadAffiliates = async () => {
    try {
      setIsLoading(true);
      const params = new URLSearchParams();
      if (filter !== 'all') {
        params.append('status', filter);
      }

      const response = await fetch(`${API_BASE_URL}/v1/affiliates/admin/affiliates?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      const data = await response.json();
      if (data.success && data.data) {
        setAffiliates(data.data.affiliates || []);
      }
    } catch (error) {
      console.error('Failed to load affiliates', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleApprove = async (id: string) => {
    if (!confirm('Approve this affiliate?')) return;

    try {
      const response = await fetch(`${API_BASE_URL}/v1/affiliates/admin/affiliates/${id}/approve`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (response.ok) {
        loadAffiliates();
        setSelectedAffiliate(null);
        toast.success('Affiliate approved successfully!', {
          duration: 3000,
          icon: '✅',
        });
      }
    } catch (error) {
      console.error('Failed to approve affiliate', error);
      toast.error('Error approving affiliate', {
        duration: 5000,
        icon: '❌',
      });
    }
  };

  const handleSuspend = async (id: string) => {
    const reason = prompt('Suspension reason:');
    if (!reason) return;

    try {
      const response = await fetch(`${API_BASE_URL}/v1/affiliates/admin/affiliates/${id}/suspend`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ reason }),
      });

      if (response.ok) {
        loadAffiliates();
        setSelectedAffiliate(null);
        toast.success('Affiliate suspended', {
          duration: 3000,
          icon: '✅',
        });
      }
    } catch (error) {
      console.error('Failed to suspend affiliate', error);
      toast.error('Error suspending affiliate', {
        duration: 5000,
        icon: '❌',
      });
    }
  };

  const handleReactivate = async (id: string) => {
    if (!confirm('Reactivate this affiliate?')) return;

    try {
      const response = await fetch(`${API_BASE_URL}/v1/affiliates/admin/affiliates/${id}/reactivate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (response.ok) {
        loadAffiliates();
        setSelectedAffiliate(null);
        toast.success('Affiliate reactivated', {
          duration: 3000,
          icon: '✅',
        });
      }
    } catch (error) {
      console.error('Failed to reactivate affiliate', error);
      toast.error('Error reactivating affiliate', {
        duration: 5000,
        icon: '❌',
      });
    }
  };

  const handleUpdateCommission = async () => {
    if (!selectedAffiliate) return;

    if (newCommissionRate < 0 || newCommissionRate > 100) {
      toast.error('Commission rate must be between 0 and 100', {
        duration: 5000,
        icon: '❌',
      });
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/v1/affiliates/${selectedAffiliate.id}/profile`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ commissionRate: newCommissionRate }),
      });

      if (response.ok) {
        loadAffiliates();
        setEditingCommission(false);
        toast.success('Commission rate updated successfully!', {
          duration: 3000,
          icon: '✅',
        });
        // Update the selected affiliate
        if (selectedAffiliate) {
          setSelectedAffiliate({
            ...selectedAffiliate,
            commission_rate: newCommissionRate,
          });
        }
      }
    } catch (error) {
      console.error('Failed to update commission', error);
      toast.error('Error updating commission rate', {
        duration: 5000,
        icon: '❌',
      });
    }
  };

  const getStatusBadge = (status: string) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
      active: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
      suspended: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
    };

    return (
      <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${colors[status as keyof typeof colors]}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const stats = {
    total: affiliates.length,
    active: affiliates.filter(a => a.status === 'active').length,
    pending: affiliates.filter(a => a.status === 'pending').length,
    totalEarnings: Number(affiliates.reduce((sum, a) => sum + (Number(a.total_earnings) || 0), 0)),
  };

  // Paginate results
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedAffiliates = affiliates.slice(startIndex, endIndex);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page
  };

  return (
    <MainLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Affiliates</h1>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            Manage affiliate applications and track performance
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          <div className="rounded-lg bg-white p-6 shadow dark:bg-gray-800">
            <div className="text-sm font-medium text-gray-500 dark:text-gray-400">Total Affiliates</div>
            <div className="mt-2 text-3xl font-bold text-gray-900 dark:text-white">{stats.total}</div>
          </div>
          <div className="rounded-lg bg-white p-6 shadow dark:bg-gray-800">
            <div className="text-sm font-medium text-gray-500 dark:text-gray-400">Active</div>
            <div className="mt-2 text-3xl font-bold text-green-600 dark:text-green-400">{stats.active}</div>
          </div>
          <div className="rounded-lg bg-white p-6 shadow dark:bg-gray-800">
            <div className="text-sm font-medium text-gray-500 dark:text-gray-400">Pending Approval</div>
            <div className="mt-2 text-3xl font-bold text-yellow-600 dark:text-yellow-400">{stats.pending}</div>
          </div>
          <div className="rounded-lg bg-white p-6 shadow dark:bg-gray-800">
            <div className="text-sm font-medium text-gray-500 dark:text-gray-400">Total Earnings Paid</div>
            <div className="mt-2 text-3xl font-bold text-indigo-600 dark:text-indigo-400">
              ${stats.totalEarnings.toFixed(2)}
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="flex gap-2">
          {['all', 'pending', 'active', 'suspended'].map((status) => (
            <button
              key={status}
              onClick={() => setFilter(status as any)}
              className={`rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
                filter === status
                  ? 'bg-indigo-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700'
              }`}
            >
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </button>
          ))}
        </div>

        {/* Table */}
        <div className="overflow-hidden rounded-lg bg-white shadow dark:bg-gray-800">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-900">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    Affiliate
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    Referral Code
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    Referrals
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    Earnings
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    Commission
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    Joined
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-gray-800">
                {isLoading ? (
                  <tr>
                    <td colSpan={8} className="px-6 py-12 text-center">
                      <div className="flex items-center justify-center">
                        <div className="h-8 w-8 animate-spin rounded-full border-4 border-indigo-600 border-t-transparent"></div>
                      </div>
                    </td>
                  </tr>
                ) : affiliates.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-6 py-12 text-center text-gray-500 dark:text-gray-400">
                      No affiliates found
                    </td>
                  </tr>
                ) : (
                  paginatedAffiliates.map((affiliate) => (
                    <tr
                      key={affiliate.id}
                      onClick={() => setSelectedAffiliate(affiliate)}
                      className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700"
                    >
                      <td className="whitespace-nowrap px-6 py-4">
                        <div className="flex flex-col">
                          <div className="font-medium text-gray-900 dark:text-white">
                            {affiliate.first_name} {affiliate.last_name}
                          </div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">{affiliate.email}</div>
                          {affiliate.company_name && (
                            <div className="text-xs text-gray-400 dark:text-gray-500">{affiliate.company_name}</div>
                          )}
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-6 py-4">
                        <code className="rounded bg-gray-100 px-2 py-1 text-sm font-mono text-gray-800 dark:bg-gray-700 dark:text-gray-300">
                          {affiliate.referral_code || affiliate.code}
                        </code>
                      </td>
                      <td className="whitespace-nowrap px-6 py-4">
                        {getStatusBadge(affiliate.status)}
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-900 dark:text-white">
                        {affiliate.total_referrals || 0}
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm font-medium text-gray-900 dark:text-white">
                        ${(Number(affiliate.total_earnings) || 0).toFixed(2)}
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm font-medium text-indigo-600 dark:text-indigo-400">
                        {affiliate.commission_rate || 20}%
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                        {new Date(affiliate.created_at).toLocaleDateString()}
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-right text-sm font-medium">
                        <div className="flex justify-end gap-2">
                          {affiliate.status === 'pending' && (
                            <button
                              onClick={(e) => { e.stopPropagation(); handleApprove(affiliate.id); }}
                              className="rounded-md bg-green-600 px-3 py-1.5 text-white hover:bg-green-700"
                            >
                              Approve
                            </button>
                          )}
                          {affiliate.status === 'active' && (
                            <button
                              onClick={(e) => { e.stopPropagation(); handleSuspend(affiliate.id); }}
                              className="rounded-md bg-red-600 px-3 py-1.5 text-white hover:bg-red-700"
                            >
                              Suspend
                            </button>
                          )}
                          {affiliate.status === 'suspended' && (
                            <button
                              onClick={(e) => { e.stopPropagation(); handleReactivate(affiliate.id); }}
                              className="rounded-md bg-indigo-600 px-3 py-1.5 text-white hover:bg-indigo-700"
                            >
                              Reactivate
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {affiliates.length > 0 && (
            <Pagination
              currentPage={currentPage}
              totalItems={affiliates.length}
              itemsPerPage={itemsPerPage}
              onPageChange={handlePageChange}
              onItemsPerPageChange={handleItemsPerPageChange}
            />
          )}
        </div>

        {/* Detail Modal */}
        {selectedAffiliate && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => {
            setSelectedAffiliate(null);
            setEditingCommission(false);
          }}>
            <div className="max-w-2xl rounded-lg bg-white p-6 dark:bg-gray-800" onClick={(e) => e.stopPropagation()}>
              <h3 className="mb-4 text-xl font-bold text-gray-900 dark:text-white">
                Affiliate Details
              </h3>

              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Name</label>
                    <p className="mt-1 text-sm text-gray-900 dark:text-white">
                      {selectedAffiliate.first_name} {selectedAffiliate.last_name}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Email</label>
                    <p className="mt-1 text-sm text-gray-900 dark:text-white">{selectedAffiliate.email}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Referral Code</label>
                    <p className="mt-1">
                      <code className="rounded bg-gray-100 px-2 py-1 text-sm font-mono text-gray-800 dark:bg-gray-700 dark:text-gray-300">
                        {selectedAffiliate.referral_code || selectedAffiliate.code}
                      </code>
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Status</label>
                    <p className="mt-1">{getStatusBadge(selectedAffiliate.status)}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Total Referrals</label>
                    <p className="mt-1 text-2xl font-bold text-gray-900 dark:text-white">
                      {selectedAffiliate.total_referrals || 0}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Total Earnings</label>
                    <p className="mt-1 text-2xl font-bold text-green-600 dark:text-green-400">
                      ${(Number(selectedAffiliate.total_earnings) || 0).toFixed(2)}
                    </p>
                  </div>
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Commission Rate
                    </label>
                    {editingCommission ? (
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          min="0"
                          max="100"
                          step="0.1"
                          value={newCommissionRate}
                          onChange={(e) => setNewCommissionRate(Number(e.target.value))}
                          className="w-32 rounded-md border border-gray-300 px-3 py-2 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                        />
                        <span className="text-gray-500 dark:text-gray-400">%</span>
                        <button
                          onClick={handleUpdateCommission}
                          className="rounded-md bg-indigo-600 px-4 py-2 text-white hover:bg-indigo-700"
                        >
                          Save
                        </button>
                        <button
                          onClick={() => {
                            setEditingCommission(false);
                            setNewCommissionRate(selectedAffiliate.commission_rate || 20);
                          }}
                          className="rounded-md px-4 py-2 text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <p className="text-2xl font-bold text-indigo-600 dark:text-indigo-400">
                          {selectedAffiliate.commission_rate || 20}%
                        </p>
                        <button
                          onClick={() => {
                            setEditingCommission(true);
                            setNewCommissionRate(selectedAffiliate.commission_rate || 20);
                          }}
                          className="ml-2 text-sm text-indigo-600 hover:text-indigo-700 dark:text-indigo-400 dark:hover:text-indigo-300"
                        >
                          Edit
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                <div className="mt-6 flex justify-end gap-3">
                  <button
                    onClick={() => setSelectedAffiliate(null)}
                    className="rounded-md px-4 py-2 text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
                  >
                    Close
                  </button>
                  {selectedAffiliate.status === 'pending' && (
                    <button
                      onClick={() => handleApprove(selectedAffiliate.id)}
                      className="rounded-md bg-green-600 px-4 py-2 text-white hover:bg-green-700"
                    >
                      Approve
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
}
